import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileCheck2, Download, CheckCircle2 } from "lucide-react";
import { TIDS } from "@/lib/testIds";

const PERKS = [
  "27 checkpoints across 5 growth areas",
  "Score your practice in 10 minutes",
  "The exact list we run before spending a dollar",
];

export default function LeadMagnet() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/lead-magnet`, {
        full_name: name || null,
        email,
      });
      setDone(true);
      if (window.posthog) window.posthog.capture("checklist_download", {});
      toast.success("Check your inbox — sending your checklist now.");
      // also trigger immediate download
      const link = document.createElement("a");
      link.href = data.download_url;
      link.download = "dental-marketing-audit-checklist.pdf";
      link.click();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid={TIDS.leadMagnetSection}
      className="py-20 md:py-28 relative"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-blue-900/25 via-white/[0.02] to-transparent p-10 md:p-14"
        >
          <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 mb-6">
                <FileCheck2 className="w-4 h-4 text-amber-400" />
                <span className="font-mono-accent text-[11px] uppercase tracking-[0.2em] text-white/80">
                  Free download
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tight text-white leading-[1.08]">
                The Dental Marketing Audit Checklist.
              </h2>
              <ul className="mt-8 space-y-3">
                {PERKS.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-white/75">
                    <CheckCircle2 className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-6">
              {done ? (
                <div className="rounded-2xl border border-amber-400/30 bg-amber-400/5 p-8 text-center">
                  <CheckCircle2 className="w-10 h-10 text-amber-400 mx-auto" />
                  <h3 className="mt-4 font-display text-2xl text-white">Sent to your inbox.</h3>
                  <p className="mt-2 text-white/60 text-sm">
                    Your download should also start automatically.
                  </p>
                  <a
                    href="/dental-marketing-audit-checklist.pdf"
                    download
                    className="mt-5 inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium"
                  >
                    <Download className="w-4 h-4" /> Download again
                  </a>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 space-y-4"
                >
                  <Input
                    data-testid={TIDS.leadMagnetName}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name (optional)"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                  />
                  <Input
                    data-testid={TIDS.leadMagnetEmail}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@practice.com"
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-12"
                    required
                  />
                  <Button
                    data-testid={TIDS.leadMagnetSubmit}
                    type="submit"
                    disabled={loading}
                    className="group w-full h-12 rounded-full bg-amber-400 text-[#070B14] hover:bg-amber-300 font-medium transition-colors active:scale-[0.98] disabled:opacity-60"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {loading ? "Sending…" : "Send me the checklist"}
                  </Button>
                  <p className="text-center text-xs text-white/40">
                    No spam. Just the checklist and the occasional growth tip.
                  </p>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
